import { useTranslation } from "react-i18next";
import { ReportWellnessSummaryData } from "../../models/interfaces/reports/report-wellness-summary-data";
import ReportGenericHeader from "./report-generic-header";
import { WellnessProgramStatus, WellnessProgramStatusTranslation } from "../../models/interfaces/wellness-program-status";

const COMPONENT_CLASS = "c-report";

// -----------------------------------------------------------------------------------------
// #region Intefaces
// -----------------------------------------------------------------------------------------

interface ReportWellnessSummaryProps {
    data: ReportWellnessSummaryData;
}

// #endregion Intefaces

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const ReportWellnessSummary: React.FC<ReportWellnessSummaryProps> = (props: ReportWellnessSummaryProps) => {
    const { data } = props;
    const { t } = useTranslation();
    return (
        <div className={`${COMPONENT_CLASS} -wellness-summary`}>
            <ReportGenericHeader companyName={data.organizationName} reportName={t('reports.wellnessSummary.reportName')} date={data.reportDate} />
            <div className="flex gap-6 pt-6 pb-6 px-10 sub-header" style={{ backgroundColor: "#F7F9FC" }}>
                <div className="w-1/3">
                    <p style={{ fontWeight: "500" }}>{t('reports.usersInReport')}</p>
                    <p>{data.totalUsers}</p>
                </div>
                <div className="w-1/3">
                    <p style={{ fontWeight: "500" }}>{t('reports.groups')}</p>
                    {data.groupNames && data.groupNames.length > 0 ?
                        <p>{data.groupNames.join(", ")}</p> :
                        <p>{data.organizationName}</p>
                    }
                </div>
                <div className="w-1/3">
                    <p style={{ fontWeight: "500" }}>{t('reports.tags')}</p>
                    {data.tagNames && data.tagNames.length > 0 &&
                        <p>{data.tagNames.join(", ")}</p>
                    }
                </div>
            </div>
            <div className="px-10 pt-6">
                <h2>{t('reports.compliance.overallStatus')}</h2>
            </div>
            <div className="engagement-container flex gap-6 pt-6 pb-4 px-10">
                <div className="engagement-items flex-grow">
                    <div className="first-row">
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item first-engagement-item`}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.averageEngagement}%`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.averageEngagement')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.sevenDayEngagement}%`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.sevenDayEngagement')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.thirtyDayEngagement}%`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.thirtyDayEngagement')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.averageDaysMissed}`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.averageDaysMissed')}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="second-row">
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.averageStreak}`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.averageStreak')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.activePrograms}`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.activePrograms')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.pendingRetest}`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.pendingRetest')}
                                </div>
                            </div>
                        </div>
                        <div className={`${COMPONENT_CLASS}__engagement__wrapper`}>
                            <div className={`${COMPONENT_CLASS}__engagement__item `}>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__count`}
                                >
                                    {`${data.enrolledUsers}`}
                                </div>
                                <div
                                    className={`${COMPONENT_CLASS}__engagement__item__name`}
                                >
                                    {t('reports.wellnessSummary.engagement.enrolledUsers')}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <hr />
            <div className={`${COMPONENT_CLASS}__section`}>
                <table className={`${COMPONENT_CLASS}__table`}>
                    <thead>
                        <tr>
                            <td>{t('reports.wellnessSummary.table.name')}</td>
                            <td>{t('reports.wellnessSummary.table.programStatus')}</td>
                            <td>{t('reports.wellnessSummary.table.daysLeft')}</td>
                            <td>{t('reports.wellnessSummary.table.daysMissed')}</td>
                            <td>{t('reports.wellnessSummary.table.currentStreak')}</td>
                            <td>{t('reports.wellnessSummary.table.sevenDayEngagement')}</td>
                            <td>{t('reports.wellnessSummary.table.thirtyDayEngagement')}</td>
                            <td>{t('reports.wellnessSummary.table.progress')}</td>
                        </tr>
                    </thead>
                    <tbody>
                        {data.userData.map((user, index) => (
                            <tr
                                className={`${COMPONENT_CLASS}__table__row`}
                                key={index}>
                                <td>{user.name}</td>
                                <td
                                    className={
                                        user.programStatus === WellnessProgramStatus.Pending
                                            ? "status-enrolled"
                                            : user.programStatus === WellnessProgramStatus.Ended
                                                ? "status-retest"
                                                : user.programStatus === WellnessProgramStatus.Active
                                                    ? "status-active"
                                                    : ""
                                    }
                                >
                                    {user.programStatus != null
                                        ? t(WellnessProgramStatusTranslation[user.programStatus])
                                        : ""}
                                </td>
                                <td>{user.daysLeft}</td>
                                <td>{user.daysMissed}</td>
                                <td>{user.currentStreak}</td>
                                <td
                                    className={
                                        user.sevenDayEngagement < 40
                                            ? "low-engagement"
                                            : ""
                                    }
                                >
                                    {`${user.sevenDayEngagement}%`}
                                </td>

                                <td
                                    className={
                                        user.thirtyDayEngagement < 50
                                            ? "low-engagement"
                                            : ""
                                    }
                                >
                                    {`${user.thirtyDayEngagement}%`}
                                </td>

                                <td className={`font-bold`}>{`${user.progressPercentage}%`}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div >
    );
}

export default ReportWellnessSummary;
